import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  getMemoryOffscreenPrefs,
  getMemoryProductStudyMode,
  loadPersistedOffscreenPrefs,
  loadPersistedProductStudyMode,
  persistOffscreenPrefs,
  persistProductStudyMode,
  setMemoryOffscreenPrefs,
  setMemoryProductStudyMode,
  type OffscreenInteractionMode,
  type OffscreenPrefs,
  type ProductStudyMode,
} from '../utils/productStudyModeStore';

type ProductStudyModeContextValue = {
  productMode: ProductStudyMode;
  setProductMode: (mode: ProductStudyMode) => void;
  offscreenPrefs: OffscreenPrefs;
  setOffscreenInteractionMode: (mode: OffscreenInteractionMode) => void;
  setOffscreenAllowGlance: (allow: boolean) => void;
  setOffscreenModel: (model: string) => void;
  setOffscreenGenerationGroups: (groups: number | "all") => void;
  hydrated: boolean;
};

const ProductStudyModeContext = createContext<ProductStudyModeContextValue>({
  productMode: getMemoryProductStudyMode(),
  setProductMode: () => {},
  offscreenPrefs: getMemoryOffscreenPrefs(),
  setOffscreenInteractionMode: () => {},
  setOffscreenAllowGlance: () => {},
  setOffscreenModel: () => {},
  setOffscreenGenerationGroups: () => {},
  hydrated: false,
});

export function ProductStudyModeProvider({ children }: { children: ReactNode }) {
  const [productMode, setProductModeState] = useState<ProductStudyMode>(getMemoryProductStudyMode());
  const [offscreenPrefs, setOffscreenPrefsState] = useState<OffscreenPrefs>(getMemoryOffscreenPrefs());
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      loadPersistedProductStudyMode(),
      loadPersistedOffscreenPrefs(),
    ]).then(([savedMode, savedPrefs]) => {
      if (cancelled) return;
      setProductModeState(savedMode);
      setOffscreenPrefsState(savedPrefs);
      setHydrated(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const setProductMode = useCallback((mode: ProductStudyMode) => {
    setMemoryProductStudyMode(mode);
    setProductModeState(mode);
    void persistProductStudyMode(mode);
  }, []);

  const setOffscreenInteractionMode = useCallback((mode: OffscreenInteractionMode) => {
    setOffscreenPrefsState((prev) => {
      const next = { ...prev, interactionMode: mode };
      setMemoryOffscreenPrefs(next);
      void persistOffscreenPrefs(next);
      return next;
    });
  }, []);

  const setOffscreenAllowGlance = useCallback((allow: boolean) => {
    setOffscreenPrefsState((prev) => {
      const next = { ...prev, allowGlance: allow };
      setMemoryOffscreenPrefs(next);
      void persistOffscreenPrefs(next);
      return next;
    });
  }, []);

  const setOffscreenModel = useCallback((model: string) => {
    setOffscreenPrefsState((prev) => {
      const next = { ...prev, osrModel: model.trim() };
      setMemoryOffscreenPrefs(next);
      void persistOffscreenPrefs(next);
      return next;
    });
  }, []);

  const setOffscreenGenerationGroups = useCallback((groups: number | 'all') => {
    setOffscreenPrefsState((prev) => {
      const next = { ...prev, generationGroups: groups === 'all' ? 'all' as const : Math.min(100, Math.max(1, Math.trunc(groups) || 1)) };
      setMemoryOffscreenPrefs(next);
      void persistOffscreenPrefs(next);
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({
      productMode,
      setProductMode,
      offscreenPrefs,
      setOffscreenInteractionMode,
      setOffscreenAllowGlance,
      setOffscreenModel,
      setOffscreenGenerationGroups,
      hydrated,
    }),
    [
      hydrated,
      offscreenPrefs,
      productMode,
      setOffscreenAllowGlance,
      setOffscreenModel,
      setOffscreenGenerationGroups,
      setOffscreenInteractionMode,
      setProductMode,
    ],
  );

  return (
    <ProductStudyModeContext.Provider value={value}>
      {children}
    </ProductStudyModeContext.Provider>
  );
}

export function useProductStudyMode(): ProductStudyModeContextValue {
  return useContext(ProductStudyModeContext);
}
